/**
 * @fileoverview Panel de diagnóstico para monitorear el estado de la conexión API
 * 
 * Implementa un componente React que muestra información detallada sobre
 * el estado de la conexión a la API, peticiones de autenticación y posibles
 * errores. Solo visible en modo desarrollo o con flag de depuración activado.
 */

import React, { useState, useEffect } from 'react';
import apiMonitor from '../utils/apiMonitor';

// Estilos inline para evitar dependencias adicionales
const styles = {
  container: {
    position: 'fixed',
    bottom: '10px',
    right: '10px',
    width: '300px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
    zIndex: 9999,
    fontFamily: 'monospace',
    fontSize: '12px',
    padding: '8px',
    maxHeight: '400px',
    overflowY: 'auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #dee2e6',
    paddingBottom: '5px',
    marginBottom: '10px'
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  title: {
    margin: '0',
    fontSize: '14px'
  },
  indicator: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    display: 'inline-block'
  },
  section: {
    marginBottom: '10px'
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '5px',
    borderBottom: '1px dashed #dee2e6',
    paddingBottom: '3px'
  },
  property: {
    display: 'grid',
    gridTemplateColumns: '40% 60%',
    marginBottom: '3px'
  },
  label: {
    color: '#6c757d',
    paddingRight: '5px'
  },
  value: {
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  healthy: {
    backgroundColor: '#28a745'
  },
  warning: {
    backgroundColor: '#ffc107'
  },
  error: {
    backgroundColor: '#dc3545'
  },
  endpoints: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
    marginTop: '5px'
  },
  endpoint: {
    padding: '2px 5px',
    fontSize: '10px',
    borderRadius: '3px',
    backgroundColor: '#e9ecef',
    cursor: 'pointer'
  },
  expandButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 5px',
    fontSize: '12px'
  },
  requestList: {
    fontSize: '11px',
    maxHeight: '150px',
    overflowY: 'auto',
    marginTop: '5px',
    border: '1px solid #e9ecef',
    padding: '5px',
    borderRadius: '3px'
  },
  request: {
    padding: '3px',
    borderBottom: '1px dashed #e9ecef',
    display: 'flex',
    justifyContent: 'space-between'
  },
  requestSuccess: {
    borderLeft: '3px solid #28a745'
  },
  requestError: {
    borderLeft: '3px solid #dc3545'
  },
  timestamp: {
    fontSize: '9px',
    color: '#6c757d'
  },
  method: {
    fontWeight: 'bold',
    marginRight: '5px'
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '10px'
  },
  button: {
    padding: '5px 10px',
    fontSize: '11px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer'
  }
};

const DiagnosticsPanel = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [health, setHealth] = useState({ status: 'unknown', details: {} });
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());
  const [recentRequests, setRecentRequests] = useState([]);
  
  // Solo mostrar en desarrollo o con flag DEBUG=true
  const showPanel = process.env.NODE_ENV !== 'production' || 
                   window.localStorage.getItem('NUBELYTICS_DEBUG') === 'true';
  
  // Actualizar el estado de salud cada 5 segundos
  useEffect(() => {
    if (!showPanel || !visible) return;
    
    const updateStatus = () => {
      const status = apiMonitor.getStatusSummary();
      const logs = apiMonitor.getLogs ? apiMonitor.getLogs() : [];
      
      // Determinar el estado de salud
      let healthStatus = 'healthy';
      if (status.indicators.unauthorizedResponses > 0 || 
          status.indicators.dnsResolutionFailures > 0) {
        healthStatus = 'error';
      } else if (status.indicators.tokenFormatErrors > 0) {
        healthStatus = 'warning';
      }
      
      setHealth({
        status: healthStatus,
        details: status
      });
      
      // Filtrar y mapear los registros recientes
      const authRequests = logs
        .filter(log => log.event.includes('api_') || log.event.includes('token'))
        .slice(-10)
        .map(log => ({
          timestamp: new Date(log.timestamp).toLocaleTimeString(),
          event: log.event,
          url: log.data.url,
          status: log.data.status,
          method: log.data.method,
          success: log.event.includes('success')
        }));
      
      setRecentRequests(authRequests);
      setRefreshTimestamp(Date.now());
    };
    
    updateStatus();
    const interval = setInterval(updateStatus, 5000);
    return () => clearInterval(interval);
  }, [visible, showPanel]);
  
  if (!showPanel) return null;
  
  const toggleVisibility = () => {
    setVisible(!visible);
    if (!visible) {
      // Forzar actualización al abrir
      setRefreshTimestamp(Date.now());
    }
  };
  
  const runApiTest = () => {
    const testEndpoint = `${health.details.apiBaseUrl || 'https://api.nubelytics.com'}/auth/health`;
    fetch(testEndpoint, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => {
      console.log(`🔍 Test de API: ${response.status} ${response.statusText}`);
      setRefreshTimestamp(Date.now());
    })
    .catch(error => {
      console.error(`🔍 Test de API falló: ${error.message}`);
      setRefreshTimestamp(Date.now());
    });
  };
  
  const clearTelemetry = () => {
    try {
      localStorage.removeItem('nubelytics_api_telemetry');
      console.log('🔍 Telemetría limpiada correctamente');
      setRefreshTimestamp(Date.now());
    } catch (e) {
      console.error(`Error al limpiar telemetría: ${e.message}`);
    }
  };
  
  // El botón flotante para abrir/cerrar
  if (!visible) {
    return (
      <button
        onClick={toggleVisibility}
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          fontSize: '18px',
          cursor: 'pointer',
          zIndex: 9999
        }}
        title="Abrir panel de diagnóstico"
      >
        🔍
      </button>
    );
  }
  
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Diagnóstico API</h3>
        <div style={styles.status}>
          <span
            style={{
              ...styles.indicator,
              ...(health.status === 'healthy' ? styles.healthy :
                 health.status === 'warning' ? styles.warning : styles.error)
            }}
          />
          <button onClick={toggleVisibility} style={styles.expandButton}>✕</button>
        </div>
      </div>
      
      <div style={styles.section}>
        <div style={styles.property}>
          <span style={styles.label}>API Base:</span>
          <span style={styles.value} title={health.details.apiBaseUrl}>
            {health.details.apiBaseUrl || 'No disponible'}
          </span>
        </div>
        <div style={styles.property}>
          <span style={styles.label}>Estado:</span>
          <span style={{
            ...styles.value,
            color: health.status === 'healthy' ? '#28a745' :
                  health.status === 'warning' ? '#ffc107' : '#dc3545'
          }}>
            {health.status === 'healthy' ? 'Saludable' : 
             health.status === 'warning' ? 'Advertencias' : 'Errores'}
          </span>
        </div>
        <div style={styles.property}>
          <span style={styles.label}>Peticiones:</span>
          <span style={styles.value}>{health.details.totalRequests || 0}</span>
        </div>
        
        {health.details.indicators && (
          <>
            <div style={styles.property}>
              <span style={styles.label}>Errores 401:</span>
              <span style={{
                ...styles.value,
                color: health.details.indicators.unauthorizedResponses > 0 ? '#dc3545' : 'inherit'
              }}>
                {health.details.indicators.unauthorizedResponses || 0}
              </span>
            </div>
            <div style={styles.property}>
              <span style={styles.label}>Errores DNS:</span>
              <span style={{
                ...styles.value,
                color: health.details.indicators.dnsResolutionFailures > 0 ? '#dc3545' : 'inherit'
              }}>
                {health.details.indicators.dnsResolutionFailures || 0}
              </span>
            </div>
          </>
        )}
      </div>
      
      <button 
        onClick={() => setExpanded(!expanded)}
        style={styles.button}
      >
        {expanded ? 'Ocultar detalles' : 'Mostrar detalles'}
      </button>
      
      {expanded && (
        <>
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Peticiones recientes</div>
            
            {recentRequests.length === 0 && (
              <div style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                No hay peticiones registradas
              </div>
            )}
            
            {recentRequests.length > 0 && (
              <div style={styles.requestList}>
                {recentRequests.map((req, index) => (
                  <div 
                    key={index}
                    style={{
                      ...styles.request,
                      ...(req.success ? styles.requestSuccess : styles.requestError)
                    }}
                  >
                    <div>
                      <span style={styles.method}>{req.method || 'GET'}</span>
                      <span title={req.url}>{req.url?.split('/').pop()}</span>
                    </div>
                    <div style={styles.timestamp}>
                      {req.status && <span>{req.status} </span>}
                      {req.timestamp}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div style={styles.buttonRow}>
            <button 
              onClick={runApiTest}
              style={{
                ...styles.button,
                backgroundColor: '#28a745'
              }}
            >
              Probar API
            </button>
            <button 
              onClick={clearTelemetry}
              style={{
                ...styles.button,
                backgroundColor: '#6c757d'
              }}
            >
              Limpiar datos
            </button>
          </div>
        </>
      )}
      
      <div style={{ fontSize: '9px', color: '#6c757d', textAlign: 'center', marginTop: '10px' }}>
        Última actualización: {new Date(refreshTimestamp).toLocaleTimeString()}
      </div>
    </div>
  );
};

export default DiagnosticsPanel;
